import type { Route, Checkpoint } from '../types';
import * as turf from '@turf/turf';
import { api } from './api';

class RouteManager {
  private routes: Route[] = [];
  private routeSubscribers: ((routes: Route[]) => void)[] = [];
  private isLoading = false;

  subscribe(callback: (routes: Route[]) => void) {
    this.routeSubscribers.push(callback);
    callback([...this.routes]);
    return () => {
      this.routeSubscribers = this.routeSubscribers.filter(cb => cb !== callback);
    };
  }

  private notify() {
    this.routeSubscribers.forEach(callback => callback([...this.routes]));
  }

  async loadFromStorage() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const routes = await api.getRoutes();
      this.routes = Array.isArray(routes) ? routes.map(route => ({
        id: route.id || `route-${Date.now()}`,
        name: route.name || 'Unnamed Route',
        color: route.color || '#000000',
        coordinates: Array.isArray(route.coordinates) ? route.coordinates : [],
        checkpoints: Array.isArray(route.checkpoints) ? route.checkpoints : [],
        totalDistance: typeof route.totalDistance === 'number' ? route.totalDistance : 0
      })) : [];
      this.notify();
    } catch (error) {
      console.error('Error loading routes:', error);
      this.routes = [];
      this.notify();
    } finally {
      this.isLoading = false;
    }
  }

  getRoutes(): Route[] {
    return [...this.routes];
  }

  async saveRoute(route: Route): Promise<void> {
    try {
      if (!this.validateRoute(route)) {
        throw new Error('Invalid route format');
      }

      const safeRoute = {
        id: route.id,
        name: route.name,
        color: route.color,
        coordinates: Array.isArray(route.coordinates) ? route.coordinates : [],
        checkpoints: Array.isArray(route.checkpoints) ? route.checkpoints : [],
        totalDistance: typeof route.totalDistance === 'number' ? route.totalDistance : 0
      };

      await api.saveRoute(safeRoute);
      const index = this.routes.findIndex(r => r.id === route.id);
      if (index >= 0) {
        this.routes[index] = { ...safeRoute };
      } else {
        this.routes.push({ ...safeRoute });
      }
      this.updateTotalDistance(this.routes[index >= 0 ? index : this.routes.length - 1]);
      this.notify();
    } catch (error) {
      console.error('Error saving route:', error);
      throw error;
    }
  }

  private validateRoute(route: Route): boolean {
    return (
      route &&
      typeof route === 'object' &&
      typeof route.id === 'string' &&
      typeof route.name === 'string' &&
      typeof route.color === 'string' &&
      Array.isArray(route.coordinates) &&
      Array.isArray(route.checkpoints) &&
      typeof route.totalDistance === 'number'
    );
  }

  async deleteRoute(id: string): Promise<void> {
    try {
      await api.deleteRoute(id);
      const index = this.routes.findIndex(r => r.id === id);
      if (index >= 0) {
        this.routes.splice(index, 1);
        this.notify();
      }
    } catch (error) {
      console.error('Error deleting route:', error);
      throw error;
    }
  }

  async addCheckpoint(routeId: string, checkpoint: Checkpoint): Promise<void> {
    try {
      await api.saveCheckpoint({ ...checkpoint, routeId });
      const route = this.routes.find(r => r.id === routeId);
      if (!route) {
        throw new Error(`Route not found: ${routeId}`);
      }

      const updatedRoute = {
        ...route,
        checkpoints: Array.isArray(route.checkpoints) ? [...route.checkpoints, checkpoint] : [checkpoint]
      };
      this.updateTotalDistance(updatedRoute);
      await this.saveRoute(updatedRoute);
    } catch (error) {
      console.error('Error adding checkpoint:', error);
      throw error;
    }
  }

  async updateCheckpoint(routeId: string, checkpoint: Checkpoint): Promise<void> {
    try {
      await api.saveCheckpoint({ ...checkpoint, routeId });
      const route = this.routes.find(r => r.id === routeId);
      if (!route) {
        throw new Error(`Route not found: ${routeId}`);
      }

      const updatedRoute = {
        ...route,
        checkpoints: Array.isArray(route.checkpoints) 
          ? route.checkpoints.map(cp => cp.id === checkpoint.id ? checkpoint : cp)
          : [checkpoint]
      };
      this.updateTotalDistance(updatedRoute);
      await this.saveRoute(updatedRoute);
    } catch (error) {
      console.error('Error updating checkpoint:', error);
      throw error;
    }
  }

  async removeCheckpoint(routeId: string, checkpointId: string): Promise<void> {
    try {
      await api.deleteCheckpoint(checkpointId);
      const route = this.routes.find(r => r.id === routeId);
      if (!route) {
        throw new Error(`Route not found: ${routeId}`);
      }

      const updatedRoute = {
        ...route,
        checkpoints: Array.isArray(route.checkpoints)
          ? route.checkpoints.filter(cp => cp.id !== checkpointId)
          : []
      };
      this.updateTotalDistance(updatedRoute);
      await this.saveRoute(updatedRoute);
    } catch (error) {
      console.error('Error removing checkpoint:', error);
      throw error;
    }
  }

  private updateTotalDistance(route: Route): void {
    try {
      if (!route || !Array.isArray(route.checkpoints)) {
        route.totalDistance = 0;
        return;
      }

      const sortedCheckpoints = [...route.checkpoints].sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        if (a.type === 'goal') return 1;
        if (b.type === 'goal') return -1;
        return 0;
      });

      if (sortedCheckpoints.length < 2) {
        route.totalDistance = 0;
        return;
      }

      const coordinates = sortedCheckpoints.map(cp => {
        if (!Array.isArray(cp.position) || cp.position.length !== 2) {
          throw new Error('Invalid checkpoint position');
        }
        return cp.position;
      });

      const line = turf.lineString(coordinates);
      route.totalDistance = turf.length(line, { units: 'kilometers' });
    } catch (error) {
      console.error('Error updating total distance:', error);
      route.totalDistance = 0;
    }
  }
}

export const routeManager = new RouteManager();